/**
 * Session Management
 * Short-lived session tokens for web chat security
 */
import crypto from 'crypto';

export interface Session {
    token: string;
    accessKey: string;
    apiUrl: string;
    createdAt: Date;
    expiresAt: Date;
    lastActivity: Date;
}

export interface SessionConfig {
    accessKey: string;
    apiUrl: string;
    ttlMinutes?: number; // Default: 30 minutes
}

const SESSION_PREFIX = 'wc_';
const DEFAULT_TTL_MINUTES = 30;

export class SessionManager {
    private sessions: Map<string, Session> = new Map();
    private cleanupInterval: NodeJS.Timeout | null = null;

    constructor() {
        // Start cleanup interval to remove expired sessions
        this.startCleanup();
    }

    /**
     * Generate a new session token
     */
    generateToken(): string {
        const randomBytes = crypto.randomBytes(16).toString('hex');
        return `${SESSION_PREFIX}${randomBytes}`;
    }

    /**
     * Create a new session
     */
    createSession(config: SessionConfig): Session {
        const token = this.generateToken();
        const now = new Date();
        const ttlMinutes = config.ttlMinutes ?? DEFAULT_TTL_MINUTES;

        const session: Session = {
            token,
            accessKey: config.accessKey,
            apiUrl: config.apiUrl,
            createdAt: now,
            expiresAt: new Date(now.getTime() + ttlMinutes * 60 * 1000),
            lastActivity: now,
        };

        this.sessions.set(token, session);
        return session;
    }

    /**
     * Validate a session token and return session if valid
     * Also extends the session TTL on successful validation
     */
    validateSession(token: string): Session | null {
        if (!token || !token.startsWith(SESSION_PREFIX)) {
            return null;
        }

        const session = this.sessions.get(token);
        if (!session) {
            return null;
        }

        // Check if expired
        if (new Date() > session.expiresAt) {
            this.sessions.delete(token);
            return null;
        }

        // Extend session TTL on activity (sliding expiration)
        session.lastActivity = new Date();
        session.expiresAt = new Date(
            session.lastActivity.getTime() + DEFAULT_TTL_MINUTES * 60 * 1000
        );

        return session;
    }

    /**
     * Get session without extending TTL
     */
    getSession(token: string): Session | null {
        if (!token || !token.startsWith(SESSION_PREFIX)) {
            return null;
        }

        const session = this.sessions.get(token);
        if (!session) {
            return null;
        }

        if (new Date() > session.expiresAt) {
            this.sessions.delete(token);
            return null;
        }

        return session;
    }

    /**
     * Invalidate/delete a session
     */
    invalidateSession(token: string): void {
        this.sessions.delete(token);
    }

    /**
     * Get remaining TTL in seconds
     */
    getTimeToLive(token: string): number {
        const session = this.getSession(token);
        if (!session) {
            return 0;
        }

        const remaining = session.expiresAt.getTime() - Date.now();
        return Math.max(0, Math.floor(remaining / 1000));
    }

    /**
     * Start periodic cleanup of expired sessions
     */
    private startCleanup(): void {
        // Cleanup every 5 minutes
        this.cleanupInterval = setInterval(() => {
            const now = new Date();
            for (const [token, session] of this.sessions.entries()) {
                if (now > session.expiresAt) {
                    this.sessions.delete(token);
                }
            }
        }, 5 * 60 * 1000);

        // Allow Node.js to exit even if interval is running
        this.cleanupInterval.unref();
    }

    /**
     * Stop cleanup interval (call when shutting down)
     */
    stopCleanup(): void {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
    }

    /**
     * Clear all sessions (for shutdown)
     */
    clearAll(): void {
        this.sessions.clear();
        this.stopCleanup();
    }

    /**
     * Get active session count (for debugging)
     */
    getActiveSessionCount(): number {
        return this.sessions.size;
    }
}

// Singleton instance
let sessionManagerInstance: SessionManager | null = null;

export function getSessionManager(): SessionManager {
    if (!sessionManagerInstance) {
        sessionManagerInstance = new SessionManager();
    }
    return sessionManagerInstance;
}

export function resetSessionManager(): void {
    if (sessionManagerInstance) {
        sessionManagerInstance.clearAll();
        sessionManagerInstance = null;
    }
}
